
import Component, { mixins } from 'vue-class-component';

import WidgetMixins from '../../../mixins/WidgetMixins';
import DialogMixins from '../../../mixins/DialogMixins';
import { Getter, namespace } from 'vuex-class';
import { ILogs, IPaginate, IPayments } from '@/types/types';
import { dateFormat, truncateContent, loadWidget } from '@/utils/helpers';
import { KX_STATUS_COLORS } from '@/utils/statusColors';
import { Watch } from 'vue-property-decorator';
import moment from 'moment';
import { printPaymentReceipt, printPaymentStatement } from '@/utils/receipts';

const paymentsModule = namespace('payments');

@Component({
  name: 'Index',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    EmptyPage: loadWidget('pages/EmptyPage'),
    ProgressBar: loadWidget('widgets/CircularProgressLoader'),
    DoubleDatePicker: loadWidget('widgets/DoubleDatePicker'),
  },
  filters: {
    dateFormat,
    truncateContent,
  },
})
export default class Index extends mixins(WidgetMixins, DialogMixins) {
  @Getter('getCreditBalance') creditBalance!: number;
  @paymentsModule.Getter('getPagination') paginate!: IPaginate & {
    limit: number;
  };
  @paymentsModule.Getter('getPaymentList') payments!: IPayments[];
  menu = false;
  renderChart = false;
  statusColors: { [key: string]: { bg: string; text: string } } = {
    PENDING: KX_STATUS_COLORS.pending,
    FAILED: KX_STATUS_COLORS.failed,
    EXPIRED: KX_STATUS_COLORS.expired,
    COMPLETED: KX_STATUS_COLORS.success,
  };
  options: IPaginate = {
    itemsPerPage: 15,
    page: 1,
    total: 0,
  };
  headers: Array<{ text: string; value: string; align?: string }> = [
    {
      text: 'Date',
      value: 'createdAt',
    },

    {
      text: 'Transaction ID',
      value: 'transactionId',
    },
    {
      text: 'Channel',
      value: 'channel',
    },
    {
      text: 'Email',
      value: 'email',
    },
    {
      text: 'Amount',
      value: 'amount',
    },
    {
      text: 'Status',
      value: 'status',
    },
    {
      text: 'Receipt',
      value: 'receipt',
      align: 'center',
    },
  ];

  chartOptions: any = {
    chart: {
      zoom: { enabled: false },
      toolbar: { show: false },
      width: '100%',
      type: 'bar',
      height: '400px',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '10%',
        endingShape: 'rounded',
      },
    },
    colors: ['rgb(7, 143, 60)'],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 3,
      colors: ['transparent'],
    },
    xaxis: {
      axisBorder: {
        show: true,
      },
      labels: {
        show: true,
        styles: {
          cssClass: 'ft font-weight-medium',
        },
      },
      axisTicks: { show: true },
      categories: [],
    },
    grid: {
      show: false,
    },
    yaxis: {
      axisBorder: { show: false },
      labels: {
        show: true,
        styles: {
          cssClass: 'ft font-weight-medium',
        },
        formatter: (value: number) => {
          return value.toFixed(0);
        },
      },
      axisTicks: { show: false },
    },
    fill: {
      opacity: 1,
    },
  };

  series: Array<{ name: string; data: number[] }> = [];

  dateFilter = '';
  rangeFrom = '';
  rangeTo = '';
  page = 1;

  @Watch('page')
  onOptionsChange(page: number): void {
    this.$store.dispatch('payments/listPayments', {
      page: page ?? 1,
      limit: this.paginate.limit ?? 15,
      query: this.dateFilter,
    });
  }

  /** the picker emits "from,to"; kept so the statement can state its period */
  onDateFilterChange(date: string): void {
    const [from, to] = `${date ?? ''}`.split(',');
    this.rangeFrom = from ?? '';
    this.rangeTo = to ?? '';
  }

  downloadReceipt(payment: IPayments): void {
    if (!printPaymentReceipt(payment)) {
      this.$store.dispatch(
        'snackBarMessage',
        'Unable to open the print dialog. Check that pop-ups are allowed.',
        { root: true }
      );
      this.$store.dispatch('snackBarVisibility', true, { root: true });
    }
  }

  downloadStatement(): void {
    printPaymentStatement(this.payments ?? [], {
      from: this.rangeFrom,
      to: this.rangeTo,
    });
  }

  filterLogsByDate(date: string): void {
    this.page = 1;
    this.onDateFilterChange(date);
    this.dateFilter = `&date=${date}`;
    this.$store.dispatch('payments/listPayments', {
      page: this.page,
      limit: this.paginate.limit,
      query: this.dateFilter,
    });
  }

  created(): void {
    this.$nextTick(() => {
      this.renderChart = true;
    });
    this.page = 1;
    this.dateFilter = `&date=${moment()
      .subtract(5, 'month')
      .format('YYYY-MM-DD')},${moment().format('YYYY-MM-DD')}`;
    this.$store.dispatch('payments/listPayments', {
      page: this.page,
      limit: 15,
      query: this.dateFilter,
    });
  }
}
